import type { MetricKey } from './types';

export const NOTE_NAMES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
];

export const METRIC_KEYS: MetricKey[] = [
  'pitchAccuracy',
  'pitchStability',
  'sustain',
  'vibrato',
  'resonance',
  'singersFormant',
];

/**
 * Reference target note: **A4 (440Hz)** — the pitch of the reference tone shipped at
 * `apps/web/public/diagnosis/reference-tone-a4.wav` (measured: 440-443Hz across the tone).
 *
 * This is the note the user *hears*, so it has to be an exact octave, not a range midpoint.
 * The octave is deliberately not "comfortable for everyone" (it used to be A3/220Hz for
 * that reason): the sustain diagnostic folds a singer's pitch to the **nearest A** before
 * measuring deviation (`sustainGuide.deviationRatio`), so a bass singing A2 and a soprano
 * singing A5 both read as centred. Only the announced/played octave lives here.
 */
export const REF_FREQ = 440;

/** Sustain goal in seconds for a full breath-support score. */
export const GOAL = 12;

/** "아리랑" melody (Hz) used to score the song stage. */
export const MELODY = [262, 294, 294, 262, 294, 349, 349, 294, 262, 220, 262];

/**
 * Reference singers by fach (language-neutral fach key → artist list).
 * `matchSingers` substring-matches these keys, so the compound keys
 * (`soprano_tenor` / `alto_bass`) resolve to the base fach list.
 */
export const SINGER_REF: Record<string, string[]> = {
  soprano: ['아리아나 그란데', '머라이어 캐리', '조수미(성악)'],
  alto: ['아델', '이하이', '에이미 와인하우스'],
  tenor: ['브루노 마스', '박효신', '파바로티(성악)'],
  bass: ['김광석', '배리 화이트', '임재범'],
};

/** Valid language-neutral voice_character keys (client + server share these). */
export const CHARACTER_KEYS = [
  'breathy',
  'pressed',
  'clear',
  'bright',
  'balanced',
  'warm',
] as const;

/**
 * Reference year for vocal-age estimation. **Injected, not hardcoded**: a constant here
 * (it was `2026`) silently makes every displayed age one year wrong the moment the year
 * turns. The engine stays pure w.r.t. its arguments — only this default reads the clock,
 * and callers that need determinism (tests, replays) pass the year explicitly.
 */
export const thisYear = (): number => new Date().getFullYear();

/** Singer's-formant energy band (Hz). */
export const SINGERS_FORMANT_LOW = 2800;
export const SINGERS_FORMANT_HIGH = 3400;
